import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { 
  SubscriptionTier,
  SubscriptionStatus,
  FeatureFlag, 
  isFeatureEnabledForTier, 
  isRoleAllowedForTier,
  getMaxOrgAdminsForTier,
  computeEffectiveTier,
  computeAccessFlags,
  getTierDisplayConfig,
  TIER_CONFIG,
} from '@/lib/subscriptionTiers';
import { toast } from 'sonner';

// Module-level deduplication: prevent multiple simultaneous edge function calls
let inflight: Promise<any> | null = null;
let lastEdgeCallAt = 0;
const EDGE_CALL_COOLDOWN_MS = 30_000; // 30 seconds minimum between Stripe calls

interface SubscriptionState {
  /** Raw stored tier from organization_subscriptions */
  tier: SubscriptionTier | null;
  /** Raw subscription status */
  subscriptionStatus: SubscriptionStatus;
  subscriptionEnd: string | null;
  loading: boolean;
  error: string | null;
}

export function useSubscription() {
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const organizationId = currentOrganization?.id;
  
  const isBetaOrg = currentOrganization?.is_beta === true;
  
  // Read org-level status field (trial-related fields removed)
  const orgSubscriptionStatus = (currentOrganization as any)?.subscription_status as string | undefined;

  const [state, setState] = useState<SubscriptionState>({
    tier: null,
    subscriptionStatus: 'expired',
    subscriptionEnd: null,
    loading: true,
    error: null,
  });

  const hasLoadedOnce = useRef(false);

  const checkSubscription = useCallback(async () => {
    if (isBetaOrg) return;

    try {
      // Only show loading spinner on the very first check to prevent sidebar flicker
      if (!hasLoadedOnce.current) {
        setState(prev => ({ ...prev, loading: true, error: null }));
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        hasLoadedOnce.current = true;
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      // Check local database for subscription record
      if (organizationId) {
        const { data: localSub } = await supabase
          .from('organization_subscriptions')
          .select('*')
          .eq('organization_id', organizationId)
          .single();

        if (localSub) {
          hasLoadedOnce.current = true;
          setState({
            tier: localSub.tier as SubscriptionTier,
            subscriptionStatus: (localSub.status as SubscriptionStatus) || 'expired',
            subscriptionEnd: localSub.current_period_end,
            loading: false,
            error: null,
          });
          // If we have a local record with active status, skip Stripe verification
          if (localSub.status === 'active') {
            return;
          }
        }
      }

      // Verify with Stripe via edge function (deduplicated + throttled)
      const now = Date.now();
      if (now - lastEdgeCallAt < EDGE_CALL_COOLDOWN_MS) {
        hasLoadedOnce.current = true;
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      if (!inflight) {
        lastEdgeCallAt = now;
        inflight = supabase.functions.invoke('check-subscription').finally(() => { inflight = null; });
      }

      const { data, error } = await inflight;
      
      if (error) {
        console.error('Error checking subscription:', error);
        hasLoadedOnce.current = true;
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return;
      }

      hasLoadedOnce.current = true;
      setState({
        tier: (data.tier as SubscriptionTier) || null,
        subscriptionStatus: data.subscribed ? 'active' : (orgSubscriptionStatus as SubscriptionStatus || 'expired'),
        subscriptionEnd: data.subscription_end,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Subscription check failed:', err);
      hasLoadedOnce.current = true;
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: err instanceof Error ? err.message : 'Unknown error' 
      }));
    }
  }, [organizationId, isBetaOrg, orgSubscriptionStatus]);

  useEffect(() => {
    if (orgLoading) return;
    if (isBetaOrg) return;
    
    checkSubscription();
    const interval = setInterval(checkSubscription, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, [checkSubscription, isBetaOrg, orgLoading]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      if (!isBetaOrg) checkSubscription();
    });
    return () => subscription.unsubscribe();
  }, [checkSubscription, isBetaOrg]);

  // ─── Centralized Entitlement Computation ───────────────────────
  // Expired or missing subscriptions resolve to tier: null (fully locked).
  const resolvedStatus: SubscriptionStatus = isBetaOrg
    ? 'active'
    : (state.subscriptionStatus !== 'expired'
        ? state.subscriptionStatus
        : (orgSubscriptionStatus as SubscriptionStatus) || state.subscriptionStatus);

  const effectiveTier = computeEffectiveTier(state.tier, resolvedStatus, isBetaOrg);
  const { hasAccess, isPaid } = computeAccessFlags(resolvedStatus, isBetaOrg);
  const effectiveLoading = orgLoading ? true : (isBetaOrg ? false : state.loading);

  // ─── Feature / Role Checks (always use effectiveTier) ──────────
  const isFeatureEnabled = useCallback((feature: FeatureFlag): boolean => {
    return isFeatureEnabledForTier(effectiveTier, feature);
  }, [effectiveTier]);

  const isRoleAllowed = useCallback((role: string): boolean => {
    return isRoleAllowedForTier(effectiveTier, role);
  }, [effectiveTier]);

  const getMaxOrgAdmins = useCallback((): number => {
    return getMaxOrgAdminsForTier(effectiveTier);
  }, [effectiveTier]);

  const startCheckout = useCallback(async (tier: SubscriptionTier) => {
    try {
      if (!organizationId) {
        toast.error('No organization found');
        return;
      }
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier, organization_id: organizationId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout');
    }
  }, [organizationId]);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) {
        const msg = typeof error.message === 'string' ? error.message : '';
        if (msg.includes('No Stripe customer')) {
          toast.info('No active paid subscription found.', {
            description: 'Please subscribe to a paid plan to access billing management.',
          });
        } else {
          toast.error('Failed to open billing management. Please try again or contact support.');
        }
        return;
      }
      if (data?.url) window.open(data.url, '_blank');
      else toast.info('No active paid subscription found.', {
        description: 'Please subscribe to a paid plan to access billing management.',
      });
    } catch (err) {
      console.error('Customer portal error:', err);
      toast.info('No active paid subscription found.', {
        description: 'Please subscribe to a paid plan to access billing management.',
      });
    }
  }, []);

  return {
    // Raw stored values
    tier: state.tier,
    // Computed entitlement (use this for all gating)
    effectiveTier,
    // Status
    subscriptionStatus: resolvedStatus,
    subscriptionEnd: state.subscriptionEnd,
    // Access flags
    hasAccess,
    isPaid,
    // Loading / error
    loading: effectiveLoading,
    error: state.error,
    // Display config
    tierConfig: getTierDisplayConfig(effectiveTier),
    // Legacy compat
    isBetaOrg,
    subscribed: hasAccess,
    // Feature checks
    isFeatureEnabled,
    isRoleAllowed,
    getMaxOrgAdmins,
    // Actions
    startCheckout,
    openCustomerPortal,
    refreshSubscription: checkSubscription,
  };
}
